import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { X, Plus, Play } from "lucide-react";
import { cn } from "@/lib/utils";
import { Activity } from '@/types/journey';

interface ActivityFieldProps {
  label: string;
  activities: Activity[];
  onChange: (activities: Activity[]) => void;
  placeholder?: string;
}

const ActivityField: React.FC<ActivityFieldProps> = ({
  label,
  activities,
  onChange,
  placeholder = "Add an activity..."
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [tempActivities, setTempActivities] = useState<Activity[]>(activities || []);
  const [newActivity, setNewActivity] = useState('');

  useEffect(() => {
    setTempActivities(activities || []);
  }, [activities]);

  const handleAddActivity = () => {
    if (!newActivity.trim()) return;
    
    const newActivityItem: Activity = {
      id: `activity-${Date.now()}`,
      text: newActivity.trim()
    };
    
    setTempActivities([...tempActivities, newActivityItem]);
    setNewActivity('');
  };

  const handleRemoveActivity = (id: string) => {
    setTempActivities(tempActivities.filter(activity => activity.id !== id));
  };

  const handleSave = () => {
    onChange(tempActivities);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setTempActivities(activities || []);
    setIsEditing(false);
    setNewActivity('');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddActivity();
    }
  };

  if (isEditing) {
    return (
      <div className="space-y-2">
        <label className="text-[11px] font-medium block text-muted-foreground">
          {label}
        </label>
        
        <div className="space-y-2">
          {/* Activity list */}
          <div className="flex flex-wrap gap-1">
            {tempActivities.map((activity, index) => (
              <div 
                key={activity.id}
                className="group bg-violet-50 dark:bg-violet-900/20 text-xs px-2 py-1 rounded-full flex items-center gap-1 border border-violet-200 dark:border-violet-800/30"
              >
                <Play className="h-3 w-3 text-violet-500 dark:text-violet-400 mr-0.5" />
                <span className="text-muted-foreground mr-0.5">{index+1}.</span>
                {activity.text}
                <button
                  type="button"
                  className="opacity-0 group-hover:opacity-100 transition-opacity ml-1 text-muted-foreground hover:text-foreground"
                  onClick={() => handleRemoveActivity(activity.id)}
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
          
          {/* Add new activity */}
          <div className="flex gap-1">
            <div className="flex-1 relative">
              <Input
                type="text"
                value={newActivity}
                onChange={(e) => setNewActivity(e.target.value)}
                placeholder={placeholder}
                className="text-xs"
                onKeyDown={handleKeyDown}
              />
              {newActivity && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-1 top-1/2 transform -translate-y-1/2 h-5 w-5 p-0"
                  onClick={() => setNewActivity('')}
                >
                  <X className="h-3 w-3" />
                </Button>
              )}
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8"
              onClick={handleAddActivity}
              disabled={!newActivity.trim()}
            >
              <Plus className="h-3 w-3" />
            </Button>
          </div>
          
          <div className="flex justify-end space-x-1">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-6 text-xs"
              onClick={handleCancel}
            >
              Cancel
            </Button>
            
            <Button
              type="button"
              variant="default"
              size="sm"
              className="h-6 text-xs"
              onClick={handleSave}
            >
              Save
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-1">
      <label className="text-[11px] font-medium block text-muted-foreground">
        {label}
      </label>
      
      {tempActivities.length > 0 ? (
        <div 
          className="flex flex-wrap gap-1 min-h-[2.5rem] cursor-pointer hover:bg-violet-50 dark:hover:bg-violet-900/20 p-1 rounded"
          onClick={() => setIsEditing(true)}
        >
          {tempActivities.map((activity, index) => (
            <div 
              key={activity.id}
              className="bg-violet-50 dark:bg-violet-900/20 text-xs px-2 py-1 rounded-full flex items-center gap-1 border border-violet-200 dark:border-violet-800/30"
            >
              <Play className="h-3 w-3 text-violet-500 dark:text-violet-400 mr-0.5" />
              <span className="text-muted-foreground mr-0.5">{index+1}.</span>
              {activity.text}
            </div>
          ))}
        </div>
      ) : (
        <div 
          className="text-xs min-h-[2.5rem] cursor-pointer hover:bg-muted/40 p-1 rounded text-muted-foreground italic"
          onClick={() => setIsEditing(true)}
        >
          {placeholder}
        </div>
      )}
    </div>
  );
};

export default ActivityField; 